import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { fromLocalInputValue, toLocalInputValue } from '../../lib/format';
import { timezoneLabel } from '../../../shared/utils';
import { cn } from '../../lib/utils';

/* ------------------------------------------------------------------ */
/* Wall-clock helpers                                                  */
/* ------------------------------------------------------------------ */

/**
 * Everything in this component works on *wall-clock* strings in the workspace
 * timezone ("2026-03-04T14:30"), never on Date objects in the browser's zone.
 * `lib/format` owns the two conversions at the boundary, so DST is handled once.
 */
interface WallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function parseWall(value: string): WallClock | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
}

function formatWall(wall: WallClock): string {
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
}

/** Sortable day key, "2026-03-04". */
function dayKey(wall: Pick<WallClock, 'year' | 'month' | 'day'>): string {
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`;
}

/** Days in a month, using UTC arithmetic so the browser zone never interferes. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 0 = Monday … 6 = Sunday. */
function weekdayIndex(year: number, month: number, day: number): number {
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

function shiftDays(wall: WallClock, days: number): WallClock {
  const shifted = new Date(Date.UTC(wall.year, wall.month - 1, wall.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: wall.hour,
    minute: wall.minute,
  };
}

interface CalendarCell {
  key: string;
  day: number;
  year: number;
  month: number;
  currentMonth: boolean;
}

/** Six rows of seven, always — the grid never changes height between months. */
function buildGrid(year: number, month: number): CalendarCell[] {
  const leading = weekdayIndex(year, month, 1);
  const total = daysInMonth(year, month);
  const cells: CalendarCell[] = [];

  const previous = addMonths(year, month, -1);
  const previousTotal = daysInMonth(previous.year, previous.month);
  for (let index = leading - 1; index >= 0; index -= 1) {
    const day = previousTotal - index;
    cells.push({
      key: dayKey({ year: previous.year, month: previous.month, day }),
      day,
      year: previous.year,
      month: previous.month,
      currentMonth: false,
    });
  }

  for (let day = 1; day <= total; day += 1) {
    cells.push({ key: dayKey({ year, month, day }), day, year, month, currentMonth: true });
  }

  const next = addMonths(year, month, 1);
  let day = 1;
  while (cells.length < 42) {
    cells.push({
      key: dayKey({ year: next.year, month: next.month, day }),
      day,
      year: next.year,
      month: next.month,
      currentMonth: false,
    });
    day += 1;
  }

  return cells;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export interface DateTimePickerProps {
  /** ISO-8601 UTC string, or null when unscheduled. */
  value: string | null;
  onChange: (isoValue: string | null) => void;
  /** IANA timezone the value is displayed in (the workspace timezone). */
  timezone: string;
  disabled?: boolean;
  /** ISO-8601 UTC lower bound — earlier days and times are not selectable. */
  min?: string;
  /** Hide the "Clear" affordance for fields that must hold a value. */
  clearable?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
}

/**
 * Wall-clock picker in the workspace timezone: calendar grid + time field +
 * quick presets. The value crossing the API is always a UTC ISO string.
 */
export function DateTimePicker({
  value,
  onChange,
  timezone,
  disabled,
  min,
  clearable = true,
  placeholder = 'Pick a date and time',
  className,
  id,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedWall = useMemo(() => parseWall(toLocalInputValue(value, timezone)), [value, timezone]);
  const nowWall = useMemo(
    () => parseWall(toLocalInputValue(Date.now(), timezone)) ?? { year: 2000, month: 1, day: 1, hour: 9, minute: 0 },
    // Re-reading "now" on every render would fight React's purity rules; the
    // minute-level drift is irrelevant for a scheduling grid.
    [timezone],
  );
  const minWall = useMemo(() => (min ? parseWall(toLocalInputValue(min, timezone)) : null), [min, timezone]);

  const [view, setView] = useState(() => ({
    year: (selectedWall ?? nowWall).year,
    month: (selectedWall ?? nowWall).month,
  }));

  // Follow the selection when it moves to another month (external edits too).
  useEffect(() => {
    if (selectedWall) setView({ year: selectedWall.year, month: selectedWall.month });
  }, [selectedWall?.year, selectedWall?.month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const commit = (wall: WallClock) => {
    onChange(fromLocalInputValue(formatWall(wall), timezone));
  };

  const time = selectedWall
    ? { hour: selectedWall.hour, minute: selectedWall.minute }
    : { hour: 9, minute: 0 };

  const grid = useMemo(() => buildGrid(view.year, view.month), [view.year, view.month]);
  const minKey = minWall ? dayKey(minWall) : null;
  const todayKey = dayKey(nowWall);
  const selectedKey = selectedWall ? dayKey(selectedWall) : null;

  const cellDisabled = (cell: CalendarCell) =>
    Boolean(minKey && dayKey({ year: cell.year, month: cell.month, day: cell.day }) < minKey);

  const selectDay = (cell: CalendarCell) => {
    const next: WallClock = {
      year: cell.year,
      month: cell.month,
      day: cell.day,
      hour: time.hour,
      minute: time.minute,
    };
    // Same day as the minimum: nudge the time forward if it would land in the past.
    if (minWall && dayKey(next) === dayKey(minWall)) {
      const beforeMin =
        next.hour < minWall.hour || (next.hour === minWall.hour && next.minute < minWall.minute);
      if (beforeMin) {
        next.hour = minWall.hour;
        next.minute = minWall.minute;
      }
    }
    commit(next);
  };

  const setTime = (raw: string) => {
    const match = /^(\d{2}):(\d{2})/.exec(raw);
    if (!match) return;
    const base = selectedWall ?? { ...nowWall, hour: 9, minute: 0 };
    commit({ ...base, hour: Number(match[1]), minute: Number(match[2]) });
  };

  const presets = useMemo(() => {
    const at = (wall: WallClock, hour: number): WallClock => ({ ...wall, hour, minute: 0 });
    const tomorrow = shiftDays(nowWall, 1);
    const daysUntilMonday = (8 - (weekdayIndex(nowWall.year, nowWall.month, nowWall.day) + 1)) % 7 || 7;
    const nextMonday = shiftDays(nowWall, daysUntilMonday);
    return [
      { label: 'Today, 5:00 PM', wall: at(nowWall, 17) },
      { label: 'Tomorrow, 9:00 AM', wall: at(tomorrow, 9) },
      { label: 'Monday, 9:00 AM', wall: at(nextMonday, 9) },
    ].filter((preset) => !minWall || formatWall(preset.wall) >= formatWall(minWall));
  }, [nowWall, minWall]);

  const label = selectedWall
    ? `${MONTHS[selectedWall.month - 1]} ${selectedWall.day}, ${selectedWall.year} · ${formatClock(selectedWall)}`
    : placeholder;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            'inline-flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-surface px-3',
            'text-left text-sm transition-colors hover:border-border-strong',
            'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35',
            'disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none',
            selectedWall ? 'text-fg' : 'text-subtle',
          )}
        >
          <CalendarDays className="size-4 shrink-0 text-subtle" aria-hidden />
          <span className="truncate">{label}</span>
        </button>

        {clearable && selectedWall && !disabled && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Clear the scheduled time"
            onClick={() => onChange(null)}
          >
            <X className="size-3.5" />
          </Button>
        )}

        <span className="whitespace-nowrap text-xs text-subtle">
          {timezone} ({timezoneLabel(timezone)})
        </span>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            aria-label="Choose a date and time"
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
            className="pap-pop absolute left-0 top-full z-40 mt-2 w-[19rem] p-3"
          >
            <div className="flex items-center justify-between pb-2">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Previous month"
                onClick={() => setView((current) => addMonths(current.year, current.month, -1))}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <p className="text-sm font-medium text-fg">
                {MONTHS[view.month - 1]} {view.year}
              </p>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Next month"
                onClick={() => setView((current) => addMonths(current.year, current.month, 1))}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>

            <div className="grid grid-cols-7 gap-1 pb-1">
              {WEEKDAYS.map((weekday) => (
                <span
                  key={weekday}
                  className="pap-tnum text-center text-[10px] font-medium uppercase tracking-wide text-subtle"
                >
                  {weekday.slice(0, 2)}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {grid.map((cell) => {
                const key = dayKey({ year: cell.year, month: cell.month, day: cell.day });
                const isSelected = key === selectedKey;
                const isToday = key === todayKey;
                const isDisabled = cellDisabled(cell);
                return (
                  <button
                    key={cell.key}
                    type="button"
                    disabled={isDisabled}
                    aria-pressed={isSelected}
                    onClick={() => selectDay(cell)}
                    className={cn(
                      'pap-tnum relative flex size-9 items-center justify-center rounded-md text-[13px]',
                      'transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
                      isSelected
                        ? 'bg-accent font-semibold text-accent-fg'
                        : cell.currentMonth
                          ? 'text-fg hover:bg-elevated'
                          : 'text-subtle hover:bg-elevated',
                      isDisabled && 'pointer-events-none opacity-30',
                    )}
                  >
                    {cell.day}
                    {isToday && !isSelected && (
                      <span className="absolute bottom-1 size-1 rounded-full bg-accent" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
              <Clock className="size-4 shrink-0 text-subtle" aria-hidden />
              <Input
                type="time"
                aria-label="Time"
                inputSize="sm"
                value={`${pad(time.hour)}:${pad(time.minute)}`}
                onChange={(event) => setTime(event.target.value)}
                className="w-32"
              />
              <span className="ml-auto text-[11px] text-subtle">{timezoneLabel(timezone)}</span>
            </div>

            {presets.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      commit(preset.wall);
                      setOpen(false);
                    }}
                    className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted transition-colors hover:border-border-strong hover:text-fg"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}

            <div className="mt-3 flex justify-end gap-2">
              {clearable && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  Clear
                </Button>
              )}
              <Button size="sm" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** "2:30 PM" from a wall clock, without touching timezone conversion again. */
function formatClock(wall: WallClock): string {
  const suffix = wall.hour >= 12 ? 'PM' : 'AM';
  const hour12 = wall.hour % 12 === 0 ? 12 : wall.hour % 12;
  return `${hour12}:${pad(wall.minute)} ${suffix}`;
}
